import {ACard, ADataGrid, ADataGridColumn, ADataGridFilter, PagedSearchParams, ASectionTitle, AMultiSelectFilter, AMultiSelectOption, AMultiSelectOptionId} from "@atiautomacao/ati-ui-library";
import {
    TextField,
    Box,
} from "@mui/material";
import * as React from "react";
import {CommandHistory} from "../../../Shared/Components/Layout/types/CommandHistoryReactQuery";
import {DateFormat, LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {QueryClient, QueryClientProvider} from "react-query";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {hideDashboardHeaderMenu} from "../../Dashboard/DashboardMenuReducer";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {isValid} from "date-fns";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES, GATEWAY_URL, POWER_STATION_IDS_FILTER_STORAGE_KEY} from "../../../Config/Constants";
import {DateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {LoadingButton} from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import Button from "@mui/material/Button";
import {CheckCircleOutlined, GroupsOutlined, HandymanOutlined, SimCardDownloadOutlined, SolarPowerOutlined} from "@mui/icons-material";
import {postAndDownloadFile} from "../../Reports/ManagementReport/utils/postAndDownloadFile";
import {useSnackbar} from "notistack";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {StatusChip} from "./StatusChip"
import CommandsByPowerStationBarChart from "./components/CommandsByPowerStationBarChart";
import CommandsByEquipmentDonutChart from "./components/CommandsByEquipmentDonutChart";
import CommandsByStatusDonutChart from "./components/CommandsByStatusDonutChart";
import CommandsByOperatorBarChart from "./components/CommandsByOperatorBarChart";
import {CommandCountByPowerStationReactQuery} from "./reactQuery/CommandCountByPowerStationReactQuery";
import {CommandCountByEquipmentReactQuery} from "./reactQuery/CommandCountByEquipmentReactQuery";
import {CommandCountByStatusReactQuery} from "./reactQuery/CommandCountByStatusReactQuery";
import {CommandCountByOperatorReactQuery} from "./reactQuery/CommandCountByOperatorReactQuery";
import {FindAllPowerStationsBasicReactQuery} from "../../Settings/PowerStation/PowerStationReactQuery";
import {usePersistedSelection} from "../../../Shared/Hooks/usePersistedSelection";
import {
    applyFiltersToColumns,
    buildDateFilterSanitizationMessage,
    buildDateFilterValidationMessage,
    mergePendingDateFilterInputValues,
    sanitizeDateFilters,
    validateAndConvertDateFilters,
} from "../../../Utils/AlarmDateFilterValidationUtils";

const appendPowerStationIds = (params: URLSearchParams, powerStationIds?: number[] | null) => {
    if (!powerStationIds || powerStationIds.length === 0) return;
    powerStationIds.forEach((id) => params.append("powerStationIds", String(id)));
};

type AuditCommandHistoryApiItem = {
    id: number | string;
    dateTimeReq?: string | null;
    dateTimeRes?: string | null;
    description?: string | null;
    ticket?: string | null;
    successRes?: boolean | null;
    equipmentName?: string | null;
    commandName?: string | null;
    message?: string | null;
    successSent?: boolean | null;
    powerStationName?: string | null;
    value?: string | number | null;
    userName?: string | null;
    skidName?: string | null;
};

type AuditCommandHistoryPageData = {
    data?: AuditCommandHistoryApiItem[];
    totalOfRecords?: number;
};

type SuccessSentSortableRow = {
    successSentSortValue?: string | null;
};

type AuditCommandHistoryRow = SuccessSentSortableRow & {
    id: number | string;
    dateTimeReq: string;
    dateTimeRes: string;
    description?: string | null;
    ticket?: string | null;
    successRes: string;
    equipmentName?: string | null;
    commandName?: string | null;
    message?: string | null;
    successSent: React.ReactNode;
    powerStationName?: string | null;
    value: string;
    userName?: string | null;
    skidName?: string | null;
};

export const getSuccessSentDisplayValue = (successSent?: boolean | null): string => {
    if (successSent === true) return "Sucesso";
    if (successSent === false) return "Falha";
    return "";
};

const renderSuccessSentStatus = (successSent?: boolean | null): React.ReactNode => {
    if (successSent === true || successSent === false) {
        return <StatusChip success={successSent} />;
    }
    return "";
};

export const getSuccessSentSortDirection = (filters: Array<ADataGridFilter>): ADataGridFilter["sort"] => {
    return filters.find((filter) => filter.name === "successSent")?.sort ?? "none";
};

export const hasActiveSort = (filters: Array<ADataGridFilter>): boolean => {
    return filters.some((filter) => filter.sort && filter.sort !== "none");
};

export const compareSuccessSentDisplayValue = (a: SuccessSentSortableRow, b: SuccessSentSortableRow): number => {
    return (a.successSentSortValue ?? "").localeCompare(
        b.successSentSortValue ?? "",
        "pt-BR",
        {sensitivity: "base"}
    );
};

const columnsCommandHistoryDataGrid: ADataGridColumn[] = [
    {
        name: 'ticket',
        label: 'Ticket',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'ticket', condition: 'equals', value: '', sort: 'none'}
    },
    {
        name: 'dateTimeReq',
        label: 'Envio',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {
            name: "dateTimeReq",
            condition: "starts-at",
            value: "",
            sort: "none",
            placeholder: 'dd/MM/yyyy HH:mm:ss'
        }
    },
    {
        name: 'powerStationName',
        label: 'Usina',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'skidName',
        label: 'Skid',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'skidName', condition: 'contains', value: '', sort: "none"}
    },
    {
        name: 'equipmentName',
        label: 'Equipamento',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'commandName',
        label: 'Telecomando',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'description',
        label: 'Descrição',
        align: 'left',
        minWidth: 10,
        visibility: false,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'value',
        label: 'Valor',
        align: 'center',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'value', condition: 'equals', value: '', sort: "none"}
    },
    {
        name: 'message',
        label: 'Mensagem',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'message', condition: 'contains', value: '', sort: "none"}
    },
    {
        name: 'dateTimeRes',
        label: 'Recebido',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {
            name: "dateTimeRes",
            condition: "starts-at",
            value: "",
            sort: "none",
            placeholder: 'dd/MM/yyyy HH:mm:ss'
        }
    },
    {
        name: 'successRes',
        label: 'Resultado',
        align: 'left',
        minWidth: 10,
        visibility: false,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'id',
        label: 'ID',
        align: 'center',
        visibility: false,
        minWidth: 10,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'userName',
        label: 'Usuário',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'userName', condition: 'contains', value: '', sort: "none"}
    },
    {
        name: 'dateTimeReqEnd',// Column to period export
        label: '',
        align: 'left',
        minWidth: 10,
        visibility: false,
        breakpoints: {
            xs: false,
            sm: false,
            md: false,
            lg: false,
            xl: false
        },
        filter: {
            name: "dateTimeReq",
            condition: "ends-at",
            value: "",
            sort: "none",
            placeholder: 'dd/MM/yyyy HH:mm:ss'
        }
    },
    {
        name: 'successSent',
        label: 'Enviado',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'successSent', condition: 'equals', value: '', sort: "none"}
    }
];

function AuditCommands(){
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const isAuthenticated = useAppSelector((state: any) => state.authentication.isAuthenticated);

    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_CSV_EXPORT, AUTHORITIES.VIEW_AUDIT
    ]);
    const isAuthorizedToSearchByExcel = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_EXCEL_EXPORT, AUTHORITIES.VIEW_AUDIT
    ]);
    const [rows, setRows] = React.useState<AuditCommandHistoryRow[]>([]);
    const [totalOfRecords, setTotalOfRecords] = useState(0);
    const isAuthorizedToAccessAudit = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_AUDIT, AUTHORITIES.VIEW_AUDIT_COMMANDS
    ]);

    const handleStartDateTime = (newValue: Date) => {
        const firstDayOfMonth = new Date(newValue.getFullYear(), newValue.getMonth(), 1);
        firstDayOfMonth.setHours(0, 0, 0, 0);
        return firstDayOfMonth;
    };

    const formatEndDateTime = (selectedDate: Date | null): Date => {
        if (!selectedDate) return new Date();

        const today = new Date();

        const isToday =
            selectedDate.getDate() === today.getDate() &&
            selectedDate.getMonth() === today.getMonth() &&
            selectedDate.getFullYear() === today.getFullYear();

        if (isToday) {
            return today;
        } else {
            const endOfDay = new Date(selectedDate);
            endOfDay.setHours(23, 59, 59, 999);
            return endOfDay;
        }
    };

    // Monta os params do grid sempre com o range dos DatePickers como fonte única do filtro de data.
    const buildGridParams = (filters: Array<ADataGridFilter>, page: number, size: number, start: Date, end: Date) => {
        const newParams = new PagedSearchParams(JSON.parse(JSON.stringify(filters)), page, size);

        const startFilter = newParams.filters.find(
            (filter: ADataGridFilter) => filter.name === "dateTimeReq" && filter.condition === "starts-at"
        );
        const endFilter = newParams.filters.find(
            (filter: ADataGridFilter) => filter.name === "dateTimeReq" && filter.condition === "ends-at"
        );

        if (startFilter) startFilter.value = LocalDateTimeFormatISO(start);
        if (endFilter) endFilter.value = LocalDateTimeFormatISO(end);

        return newParams;
    };

    const [startDateTime, setStartDateTime] = React.useState<Date>(handleStartDateTime(new Date()));
    const [endDateTime, setEndDateTime] = React.useState<Date>(new Date());
    const [columns, setColumns] = useState(() => columnsCommandHistoryDataGrid);

    const [pagedSearchParams, setPagedSearchParams] = useState(
        buildGridParams(extractFiltersFromColumns(columnsCommandHistoryDataGrid), 0, 50, handleStartDateTime(new Date()), new Date())
    );

    const [isDownloadCsv, setIsDownloadCsv] = useState(false);
    const [isDownloadExcel, setIsDownloadExcel] = useState(false);

    const {data: powerStationsData} = FindAllPowerStationsBasicReactQuery();

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => (powerStationsData ?? []).map((ps: any) => ({id: ps.id, label: ps.name})),
        [powerStationsData]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map((o) => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isInitialized: powerStationsInitialized,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    const powerStationIdsForRequest = useMemo<number[] | null>(() => {
        if (!powerStationsInitialized) return null;
        if (selectedPowerStationIds.length === 0) return [];
        if (selectedPowerStationIds.length === powerStationOptions.length) return null;
        return selectedPowerStationIds.map((id) => Number(id));
    }, [selectedPowerStationIds, powerStationOptions, powerStationsInitialized]);

    const { data, isSuccess } = CommandHistory(pagedSearchParams, isAuthenticated, isAuthorizedToAccessAudit, undefined, powerStationIdsForRequest);

    const { data: chartData } = CommandCountByPowerStationReactQuery(
        LocalDateTimeFormatISO(startDateTime),
        LocalDateTimeFormatISO(endDateTime),
        isAuthenticated && isAuthorizedToAccessAudit,
        powerStationIdsForRequest
    );

    const { data: equipmentChartData } = CommandCountByEquipmentReactQuery(
        LocalDateTimeFormatISO(startDateTime),
        LocalDateTimeFormatISO(endDateTime),
        isAuthenticated && isAuthorizedToAccessAudit,
        powerStationIdsForRequest
    );

    const { data: statusChartData } = CommandCountByStatusReactQuery(
        LocalDateTimeFormatISO(startDateTime),
        LocalDateTimeFormatISO(endDateTime),
        isAuthenticated && isAuthorizedToAccessAudit,
        powerStationIdsForRequest
    );

    const { data: operatorChartData } = CommandCountByOperatorReactQuery(
        LocalDateTimeFormatISO(startDateTime),
        LocalDateTimeFormatISO(endDateTime),
        isAuthenticated && isAuthorizedToAccessAudit,
        powerStationIdsForRequest
    );

    useEffect(() => {
        dispatch(hideDashboardHeaderMenu())
    }, [dispatch]);

    const updateRows = useCallback((data: AuditCommandHistoryPageData | undefined, filters: Array<ADataGridFilter>) => {
        const successSentSort = getSuccessSentSortDirection(filters);
        const keepResponseOrder = successSentSort === "none" && hasActiveSort(filters);
        const filteredData = data?.data?.map((item): AuditCommandHistoryRow => {
            return {
                id: item.id,
                dateTimeReq: item.dateTimeReq != null ? DateFormat(item.dateTimeReq) : "",
                dateTimeRes: item.dateTimeRes != null ? DateFormat(item.dateTimeRes) : "",
                description: item?.description,
                ticket: item?.ticket,
                successRes: item?.successRes ? "Sucesso" : "Falha",
                equipmentName: item?.equipmentName,
                commandName: item?.commandName,
                message: item?.message,
                successSent: renderSuccessSentStatus(item?.successSent),
                successSentSortValue: getSuccessSentDisplayValue(item?.successSent),
                powerStationName: item?.powerStationName,
                value: item.value != null ? String(item.value) : "",
                userName: item?.userName,
                skidName: item?.skidName
            };
        }).sort((a, b) => {
            if (successSentSort === "asc") {
                return compareSuccessSentDisplayValue(a, b);
            }
            if (successSentSort === "desc") {
                return compareSuccessSentDisplayValue(b, a);
            }
            if (keepResponseOrder) {
                return 0;
            }
            return new Date(b.dateTimeReq).getTime() - new Date(a.dateTimeReq).getTime();
        });

        setRows(filteredData ?? []);
    }, []);

    useEffect(() => {
        if (isSuccess && data?.totalOfRecords !== undefined) {
            setTotalOfRecords(data?.totalOfRecords)
        }
    }, [isSuccess, data?.totalOfRecords]);

    useEffect(() => {
        updateRows(data, pagedSearchParams.filters);
    }, [data, pagedSearchParams.filters, updateRows]);

    const handleStartChange = (newValue: Date | null) => {
        const next = newValue ?? handleStartDateTime(new Date());
        if (!isValid(next)) {
            enqueueSnackbar("Informe uma data válida no campo Início.", {variant: 'error'});
            return;
        }
        if (next > endDateTime) {
            enqueueSnackbar("A data inicial não pode ser maior que a data final", {variant: 'warning'});
            return;
        }
        setStartDateTime(next);
    };

    const handleEndChange = (newValue: Date | null) => {
        const next = formatEndDateTime(newValue);
        if (!isValid(next)) {
            enqueueSnackbar("Informe uma data válida no campo Término.", {variant: 'error'});
            return;
        }
        if (startDateTime > next) {
            enqueueSnackbar("A data final não pode ser menor que a data inicial", {variant: 'warning'});
            return;
        }
        setEndDateTime(next);
    };

    // Aplica o range automaticamente no grid quando a data muda (pula o 1º render, que já vem filtrado).
    const isFirstDateEffect = useRef(true);
    useEffect(() => {
        if (isFirstDateEffect.current) {
            isFirstDateEffect.current = false;
            return;
        }
        setPagedSearchParams(prev => buildGridParams(prev.filters, prev.page, prev.size, startDateTime, endDateTime));
    }, [startDateTime, endDateTime]);

    const showDateFilterSanitizationMessage = useCallback((fieldLabels: Array<string>) => {
        enqueueSnackbar(buildDateFilterSanitizationMessage(fieldLabels), {variant: 'warning'});
    }, [enqueueSnackbar]);

    const showDateFilterValidationMessage = useCallback((fieldLabels: Array<string>) => {
        enqueueSnackbar(buildDateFilterValidationMessage(fieldLabels), {variant: 'error'});
    }, [enqueueSnackbar]);

    const validatePagedSearchParams = useCallback((params: PagedSearchParams): PagedSearchParams | null => {
        const validationResult = validateAndConvertDateFilters(params.filters);

        if (!validationResult.isValid) {
            showDateFilterValidationMessage(validationResult.invalidFieldLabels);
            return null;
        }

        return new PagedSearchParams(validationResult.filters, params.page, params.size);
    }, [showDateFilterValidationMessage]);

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        const sanitizedResult = sanitizeDateFilters(dataGridFilters);

        setColumns(applyFiltersToColumns(columnsCommandHistoryDataGrid, sanitizedResult.filters));

        if (sanitizedResult.sanitizedFieldLabels.length > 0) {
            showDateFilterSanitizationMessage(sanitizedResult.sanitizedFieldLabels);
            return;
        }

        const newPageParams = buildGridParams(sanitizedResult.filters, page, pageSize, startDateTime, endDateTime);
        const successSentFilter = newPageParams.filters.find((filter: ADataGridFilter) => filter.name === "successSent");

        if(successSentFilter && successSentFilter.value.length > 0) {
            const input = successSentFilter.value
            const fail = 'falha'.includes(input.toLowerCase());
            const sucess = 'sucesso'.includes(input.toLowerCase());
            successSentFilter.value = (fail ? false : sucess ? true : '').toString()
        }

        const validatedParams = validatePagedSearchParams(newPageParams);

        if (!validatedParams) return;

        setPagedSearchParams(
            validatedParams
        );
    }, [startDateTime, endDateTime, showDateFilterSanitizationMessage, validatePagedSearchParams]);

    const getValidatedExportParams = (): PagedSearchParams | null => {
        const currentFilters = mergePendingDateFilterInputValues(
            "command-audit-grid",
            extractFiltersFromColumns(columns),
            columns
        );
        const sanitizedResult = sanitizeDateFilters(currentFilters);

        setColumns(applyFiltersToColumns(columnsCommandHistoryDataGrid, sanitizedResult.filters));

        if (sanitizedResult.sanitizedFieldLabels.length > 0) {
            showDateFilterSanitizationMessage(sanitizedResult.sanitizedFieldLabels);
            return null;
        }

        const validationResult = validateAndConvertDateFilters(sanitizedResult.filters);

        if (!validationResult.isValid) {
            showDateFilterValidationMessage(validationResult.invalidFieldLabels);
            return null;
        }

        const newPageParams = buildGridParams(
            validationResult.filters,
            pagedSearchParams.page,
            pagedSearchParams.size,
            startDateTime,
            endDateTime
        );

        return validatePagedSearchParams(newPageParams);
    };

    const getOnClickByType = (type: string) => {
        const validatedParams = getValidatedExportParams();
        if (!validatedParams) return;

        if (type === "excel") {
            setIsDownloadExcel(true);
            return handleDownloadExcel(validatedParams)
        }
        setIsDownloadCsv(true);
        return handleDownloadCSV(validatedParams)

    }

    const handleDownloadExcel = (newPageParams: PagedSearchParams) => {
        const apiUrl = GATEWAY_URL+`api/audit/command-history/excel?`
        const exportParams = newPageParams.toURLSearchParams();
        appendPowerStationIds(exportParams, powerStationIdsForRequest);
        postAndDownloadFile(apiUrl, exportParams, 'auditoria_de_comandos.xlsx',
            () => {
                enqueueSnackbar("Sucesso ao baixar o Excel", {variant: 'success'});
                setIsDownloadExcel(false);
            },
            (error) => {
                if (error?.response?.status === 404) {
                    enqueueSnackbar("Não há dados no período selecionado.", {variant: 'warning'});
                } else {
                    enqueueSnackbar("Erro ao baixar o Excel.", {variant: 'error'});
                }
                setIsDownloadExcel(false);
            },
            () => {
                enqueueSnackbar("Dados não encontrados para esse período", {variant: 'warning'});
                setIsDownloadExcel(false);
            });
    };

    const handleDownloadCSV = (newPageParams: PagedSearchParams) => {
        const apiUrl = GATEWAY_URL+`api/audit/command-history/csv?`
        const exportParams = newPageParams.toURLSearchParams();
        appendPowerStationIds(exportParams, powerStationIdsForRequest);
        postAndDownloadFile(apiUrl, exportParams, 'auditoria_de_comandos.csv',
            () => {
                enqueueSnackbar("Sucesso ao baixar o CSV", {variant: 'success'});
                setIsDownloadCsv(false);
            },
            (error) => {
                if (error?.response?.status === 404) {
                    enqueueSnackbar("Não há dados no período selecionado.", {variant: 'warning'});
                } else {
                    enqueueSnackbar("Erro ao baixar o CSV.", {variant: 'error'});
                }
                setIsDownloadCsv(false);
            },
            () => {
                enqueueSnackbar("Dados não encontrados para esse período", {variant: 'warning'});
                setIsDownloadCsv(false);
            });

    };


    const isMediumScreen = window.innerWidth <= 1340;
    return (
            <>
                <Box sx={{flex: 1, minWidth: 0, mb: 0, marginLeft: 1}}>
                    <ASectionTitle
                        styleContainer={{flexDirection:isMediumScreen ? 'column' : 'row', padding: '0 0 24px 0',justifyContent: 'flex-start'}}
                        titleLabel="Auditoria de Comandos"
                        contentChildrenStyle={{justifyContent: isMediumScreen ? 'flex-start' : 'flex-end'}}
                        styleContainerTitle={{ display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end', padding: '0px',
                            marginBottom: 1
                        }}


                        contentChildren={
                             <LocalizationProvider dateAdapter={AdapterDateFns}>
                                 <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
                                   <AMultiSelectFilter
                                       options={powerStationOptions}
                                       selectedIds={selectedPowerStationIds}
                                       onApply={setSelectedPowerStationIds}
                                       triggerLabel="Usinas"
                                       searchPlaceholder="Buscar usina..."
                                   />
                                   <DateTimePicker
                                       label="Data inicial:"
                                       ampm={false}
                                       inputFormat='dd/MM/yyyy'
                                       views={['day']}
                                       value={startDateTime}
                                       onChange={handleStartChange}
                                       renderInput={(params: any) => <TextField sx={{ width: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', color: 'gray', height: '40px' } }} {...params} />}
                                   />
                                   <DateTimePicker
                                       label="Data final:"
                                       ampm={false}
                                       inputFormat='dd/MM/yyyy'
                                       views={['day']}
                                       value={endDateTime}
                                       onChange={handleEndChange}
                                       renderInput={(params: any) => <TextField  sx={{ width: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', color: 'gray', height: '40px' } }} {...params} />}
                                   />
                                 </Box>
                             </LocalizationProvider>
                        }
                    />
                </Box>
                <Box sx={{display: 'flex', gap: 2, marginLeft: 1, marginBottom: 2, flexDirection: isMediumScreen ? 'column' : 'row'}}>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <ACard
                            title="Comandos por Operador"
                            iconTitle={{
                                showIcon: true,
                                kind: 'mui',
                                iconMaterialComponent: GroupsOutlined,
                                iconTitleStyle: {color: '#2D5DE7'},
                            }}
                        >
                            <CommandsByOperatorBarChart data={operatorChartData}/>
                        </ACard>
                    </Box>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <ACard
                            title="Comandos por Equipamento"
                            iconTitle={{
                                showIcon: true,
                                kind: 'mui',
                                iconMaterialComponent: HandymanOutlined,
                                iconTitleStyle: {color: '#2D5DE7'},
                            }}
                        >
                            <CommandsByEquipmentDonutChart data={equipmentChartData}/>
                        </ACard>
                    </Box>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <ACard
                            title="Comandos por Status"
                            iconTitle={{
                                showIcon: true,
                                kind: 'mui',
                                iconMaterialComponent: CheckCircleOutlined,
                                iconTitleStyle: {color: '#2D5DE7'},
                            }}
                        >
                            <CommandsByStatusDonutChart data={statusChartData}/>
                        </ACard>
                    </Box>
                </Box>
                <Box sx={{marginLeft: 1, marginBottom: 3}}>
                    <ACard
                        title="Telecomandos por Usina"
                        iconTitle={{
                            showIcon: true,
                            kind: 'mui',
                            iconMaterialComponent: SolarPowerOutlined,
                            iconTitleStyle: {color: '#2D5DE7'},
                        }}
                    >
                        <CommandsByPowerStationBarChart data={chartData}/>
                    </ACard>
                </Box>
                <ACard
                   styleProps={{
                       headerStyle:{height: 0, margin: 0, padding: 0},
                       contentStyle:{padding:0},
                       cardStyle: {borderRadius: "0 0 8px 8px", marginLeft: 1}
                   }}>
                   <Box sx={{ position: "relative" }}>
                   <ADataGrid
                        headerStyle={{borderRadius: 0}}
                        tableContainerStyle={{borderRadius: 0}}
                        ariaLabel={'command-audit-grid'}
                        hideFilters={false}
                        showFilterOptions={false}
                        hideSelection={true}
                        size={"small"}
                        columns={columns}
                        rows={rows}
                        page={pagedSearchParams.page}
                        loading={false}
                        totalOfRecords={totalOfRecords}
                        onFiltersChange={handleOnFiltersChange}
                        rowsPerPage={pagedSearchParams.size}
                        actionColumnWidth={50}
                        maxHeight={700}
                   />
                        <Box
                            sx={{
                                position: "absolute",
                                left: 16,
                                bottom: 6,
                                display: "flex",
                                gap: 1,
                                zIndex: 1,
                            }}
                        >
                            {isDownloadCsv ?
                                <LoadingButton
                                    loading
                                    loadingPosition="end"
                                    endIcon={<SaveIcon sx={{ fontSize: 20 }} />}
                                >
                                    Carregando..
                                </LoadingButton>
                                :
                                <Button
                                    size="medium"
                                    variant="text"
                                    startIcon={<SimCardDownloadOutlined fontSize="large" />}
                                    onClick={() => getOnClickByType("csv")}
                                    disabled={!isAuthorizedToSearchByCsv || totalOfRecords === 0}
                                >
                                    CSV
                                </Button>}
                            {
                                isDownloadExcel ?
                                    <LoadingButton
                                        loading
                                        loadingPosition="end"
                                        endIcon={<SaveIcon sx={{ fontSize: 20 }} />}
                                    >
                                        Carregando..
                                    </LoadingButton>
                                    :
                                    <Button
                                        size="medium"
                                        variant="text"
                                        startIcon={<SimCardDownloadOutlined fontSize="large" />}
                                        onClick={() => getOnClickByType("excel")}
                                        disabled={!isAuthorizedToSearchByExcel || totalOfRecords === 0}
                                    >
                                        Excel
                                    </Button>
                            }
                        </Box>
                    </Box>
                </ACard>
            </>
    )
}

export default function AuditCommandsPage() {
    const queryClient = new QueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <AuditCommands/>
        </QueryClientProvider>
    )
}
